import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { MAIN_ELEMENT_ID } from '@/lib/landmarks';

/**
 * Every element Tab can land on inside a container.
 *
 * @since 1.3.0
 */
export const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Returns focus to whatever opened an overlay, or to the main landmark when that element
 * has since left the document.
 *
 * @since 1.3.0
 * @param element - the element that held focus before the overlay opened
 * @returns void
 */
export function restoreFocus(element: HTMLElement | null): void {
    if (element !== null && element.isConnected) {
        element.focus();

        return;
    }

    document.getElementById(MAIN_ELEMENT_ID)?.focus();
}

/**
 * Keeps Tab inside a container while it is active, calls back on Escape, and returns focus
 * to the element that had it once the container deactivates.
 *
 * @since 1.3.0
 * @param containerRef - the overlay element to trap focus within
 * @param isActive - whether the trap is engaged
 * @param onEscape - called when Escape is pressed, or null when Escape should do nothing
 * @returns void
 */
export function useFocusTrap(
    containerRef: RefObject<HTMLElement | null>,
    isActive: boolean,
    onEscape: (() => void) | null,
): void {
    const restoreRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!isActive) {
            return;
        }

        restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

        return () => {
            restoreFocus(restoreRef.current);
        };
    }, [isActive]);

    useEffect(() => {
        if (!isActive) {
            return;
        }

        /**
         * Handles Escape and wraps Tab at either end of the container.
         *
         * @since 1.3.0
         * @param event - the keyboard event
         * @returns void
         */
        function handleKeyDown(event: KeyboardEvent): void {
            if (event.key === 'Escape') {
                onEscape?.();

                return;
            }

            if (event.key !== 'Tab') {
                return;
            }

            const container = containerRef.current;

            if (container === null) {
                return;
            }

            const focusable = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (first === undefined || last === undefined) {
                event.preventDefault();
                container.focus();

                return;
            }

            const active = document.activeElement;

            // focus outside the container, or on the container itself, is pulled into the cycle
            if (!(active instanceof HTMLElement) || !container.contains(active) || active === container) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();

                return;
            }

            if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [containerRef, isActive, onEscape]);
}
